import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchCount } from '@app/src/api/fetchHooks'
import { COMPANY_QUESTIONNAIRE_QUESTION_KEYS } from '@app/src/components/CreateNewRequestModal/CompanyQuestionnaire'
import ProviderSelectorFilterDrawer, {
  PROVIDER_SELECTOR_ALLOWED_FILTERS,
} from '@app/src/components/CreateNewRequestModal/ProviderSelectorFilterDrawer'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewFilters from '@app/src/components/Drawer/Views/DrawerViewFilters'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import { ListboxComponent } from '@app/src/components/Form/SelectVirtualList'
import { useProvidersWithExistingInquiries } from '@app/src/hooks/providersWithExistingInquiries'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import { useQueryState } from '@app/src/hooks/queryState'
import AllFiltersButton from '@app/src/pages/ResourceCollection/Filters/AllFiltersButton'
import Filters from '@app/src/pages/ResourceCollection/Filters/Filters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { SortOrder } from '@app/src/types/filter'
import type { Provider } from '@app/src/types/organizations'
import { Alert, Box } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import type { Control, UseFormMethods } from 'react-hook-form'
import { useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

interface ProviderSelectorProps {
  control: Control
  setValue: UseFormMethods['setValue']
  includeOnlyLinked?: boolean
  templateIds?: number[]
  periodName?: string | null
}

const ProviderSelectorWithFilters: React.FC<ProviderSelectorProps> = ({
  control,
  setValue,
  includeOnlyLinked = true,
  templateIds,
  periodName,
}) => {
  const { formatMessage } = useIntl()
  const apiFilters = useGetApiQueryFilters(PROVIDER_SELECTOR_ALLOWED_FILTERS)
  const { openDrawer } = useDrawer()
  const [, setQueryFilters] = useQueryState('filters', [])

  //Hardcoded for now since it is very hard to get or set its height. It will also become bigger when you select many options
  //We need the button to have the same height as the select has initially
  const initialSelectHeight = 53.65

  const onFilterChanged = () => setValue(COMPANY_QUESTIONNAIRE_QUESTION_KEYS.RESPONDERS, [])

  const isLinkedFilter = {
    name: 'linked',
    filters: [
      {
        value: 'true',
        operator: Operators.EqualTo,
      },
    ],
  }

  const implicitFilters = includeOnlyLinked ? [isLinkedFilter] : []

  const { items: providers, count } = useFetchCollectionWithPost<Provider>({
    endpoint: endpoints.providersCollection,
    payload: {
      include: ['organization'],
      filter: [...apiFilters, ...implicitFilters],
      sort: { target: 'name', order: SortOrder.ASCENDING },
      pagination: { itemsPerPage: 5_000_000, pageNumber: 1 },
    },
    key: FetchKey.Provider,
  })

  const { count: unfilteredProviderCount } = useFetchCount({
    key: FetchKey.ProviderCount,
    endpoint: endpoints.providerCount,
    payload: implicitFilters,
  })

  const selectedTemplateIds = templateIds ?? []
  const isSingleTemplateSelected = selectedTemplateIds.length === 1

  const { providerIdsWithExistingInquiry } = useProvidersWithExistingInquiries({
    templateIds: selectedTemplateIds,
    periodName,
    enabled: selectedTemplateIds.length > 0,
  })

  const providerOptions = providers.map(provider => {
    const hasExistingInquiry = isSingleTemplateSelected && providerIdsWithExistingInquiry.has(provider.id)

    return {
      label: provider.name,
      disabled: hasExistingInquiry,
      value: {
        name: provider.name,
        id: provider.id,
        organizationId: provider.organizationId,
      },
    }
  })

  const shouldWarnAboutExistingRequests =
    selectedTemplateIds.length > 1 && providers.some(provider => providerIdsWithExistingInquiry.has(provider.id))

  const disabledProviderIds = useMemo(
    () => new Set(providerOptions.filter(option => option.disabled).map(option => option.value.id)),
    [providerOptions],
  )

  const selectedResponders =
    (useWatch({ control, name: COMPANY_QUESTIONNAIRE_QUESTION_KEYS.RESPONDERS }) as Partial<Provider>[] | undefined) ??
    []

  useEffect(() => {
    if (!selectedResponders.length) return

    const allowedResponders = selectedResponders.filter(
      responder => responder.id === undefined || !disabledProviderIds.has(responder.id),
    )
    if (allowedResponders.length !== selectedResponders.length) {
      setValue(COMPANY_QUESTIONNAIRE_QUESTION_KEYS.RESPONDERS, allowedResponders)
    }
  }, [selectedResponders, disabledProviderIds, setValue])

  const fieldLabel = apiFilters.length
    ? formatMessage(
        { id: 'form.createRequest.selectCompaniesLabelFiltered' },
        { filteredCount: count, unfilteredCount: unfilteredProviderCount },
      )
    : formatMessage({ id: 'form.createRequest.selectCompaniesLabel' })

  useEffect(() => {
    setQueryFilters([])
    return () => setQueryFilters([])
  }, [])

  return (
    <Box>
      {shouldWarnAboutExistingRequests && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {formatMessage({ id: 'form.createRequest.alreadyRequestedRecipientsWarning' })}
        </Alert>
      )}
      <Box display="flex" alignItems="flex-end" marginRight={1}>
        <Box flex={1} marginRight={2}>
          <Select<Partial<Provider>, Provider>
            name={COMPANY_QUESTIONNAIRE_QUESTION_KEYS.RESPONDERS}
            fieldLabel={fieldLabel}
            control={control}
            required
            enableAutoSelect
            enableSelectAll
            multiple
            forceFetch
            disableListWrap
            ListboxComponent={ListboxComponent}
            options={providerOptions}
            findSelectedValue={(value, option): boolean => value?.id === option?.value?.id}
            getOptionKey={option => option.value?.id?.toString() ?? ''}
          />
        </Box>
        <Filters allowedFilters={PROVIDER_SELECTOR_ALLOWED_FILTERS} resetPage={onFilterChanged}>
          {({ inDrawerFiltersCount }) => (
            <AllFiltersButton
              inDrawerFiltersCount={inDrawerFiltersCount}
              openDrawer={() =>
                openDrawer(
                  <DrawerViewFilters allowedFilters={PROVIDER_SELECTOR_ALLOWED_FILTERS} resetPage={onFilterChanged}>
                    <Box px={2}>
                      <ProviderSelectorFilterDrawer implicitFilters={implicitFilters} />
                    </Box>
                  </DrawerViewFilters>,
                  { showAboveDialogs: true },
                )
              }
              height={initialSelectHeight}
            />
          )}
        </Filters>
      </Box>
    </Box>
  )
}

export default ProviderSelectorWithFilters
